import {rpAppLink} from "@pg-mono/rp-routes";
import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {getCommercialsRoomRangeSectionTexts} from "./common_texts/get_commercial_room_range_section_texts";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const commercialOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        commercialsPriceM2Avg: props.region.stats?.properties_commercials_ranges_price_m2_avg || 0,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        commercialPriceMin: props.region.stats?.properties_commercials_ranges_price_min || 0,
        commercialsCountReady: props.region.stats?.properties_commercials_count_ready || 0,
        commercialsCountReadyInThreeMonths: props.region.stats?.properties_commercials_count_ready_in_3m || 0,
        commercialsCountReadyInSixMonths: props.region.stats?.properties_commercials_count_ready_in_6m || 0,
        commercialsCountReadyInOneYear: props.region.stats?.properties_commercials_count_ready_in_12m || 0,
        commercialsLatestConstructionDate: props.region.stats?.properties_commercials_latest_construction_date || null,
        commercialsOffersCountForSale: props.region.stats?.offers_commercials_count_for_sale || 0,
        commercialsPropertiesCountForSale: props.region.stats?.properties_commercials_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0
    };

    return [
        {
            headline: `Nowe lokale użytkowe na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.commercialsOffersCountForSale,
                    propertiesCountForSale: variables.commercialsPropertiesCountForSale,
                    offerTypeName: "nowych lokali użytkowych"
                }),
                ...getCommercialsRoomRangeSectionTexts(props)
            ]
        },
        {
            headline: `Ile kosztują nowe lokale użytkowe ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        variables.commercialPriceMin > 0 &&
                            `Najtańszy lokal użytkowy ${variables.regionNameDeclensionWhere} dostępny w sprzedaży kosztuje ${priceFormat(
                                variables.commercialPriceMin
                            )}. `,
                        variables.commercialsPriceM2Avg > 0 &&
                            `Aktualnie średnia cena za m2 lokali użytkowych ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(
                                variables.commercialsPriceM2Avg
                            )}.`
                    ].filter(Boolean) as string[]
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach lokali użytkowych ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na lokale użytkowe.`
                      }
            ]
        },
        {
            headline: `${variables.regionName} - lokale użytkowe gotowe do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountForSale: variables.commercialsPropertiesCountForSale,
                    offersCountReady: variables.commercialsCountReady,
                    offersCountReadyInThreeMonths: variables.commercialsCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.commercialsCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.commercialsCountReadyInOneYear,
                    offerTypeNamePluralize: ["lokal użytkowy", "lokale użytkowe", "lokali użytkowych"],
                    offerTypeNameWithDeclensionOfReadyPluralize: [
                        "lokal użytkowy zostanie oddany",
                        "lokale użytkowe zostaną oddane",
                        "lokali użytkowych zostanie oddanych"
                    ],
                    offersLatestConstructionDate: variables.commercialsLatestConstructionDate
                }),
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Szukasz atrakcyjnej nieruchomości do zamieszkania? Sprawdź `,
                        variables.flatsPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${variables.regionSlug}`}),
                                  text: `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`,
                        ` i `,
                        variables.housesPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${variables.regionSlug}`}),
                                  text: `nowych domów ${variables.regionNameDeclensionWhere}`
                              }
                            : `nowych domów ${variables.regionNameDeclensionWhere}`,
                        `.`
                    ]
                }
            ]
        }
    ];
};
import {rpAppLink} from "@pg-mono/rp-routes";
import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {getCommercialsRoomRangeSectionTexts} from "./common_texts/get_commercial_room_range_section_texts";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const commercialOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        commercialsPriceM2Avg: props.region.stats?.properties_commercials_ranges_price_m2_avg || 0,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        commercialPriceMin: props.region.stats?.properties_commercials_ranges_price_min || 0,
        commercialsCountReady: props.region.stats?.properties_commercials_count_ready || 0,
        commercialsCountReadyInThreeMonths: props.region.stats?.properties_commercials_count_ready_in_3m || 0,
        commercialsCountReadyInSixMonths: props.region.stats?.properties_commercials_count_ready_in_6m || 0,
        commercialsCountReadyInOneYear: props.region.stats?.properties_commercials_count_ready_in_12m || 0,
        commercialsLatestConstructionDate: props.region.stats?.properties_commercials_latest_construction_date || null,
        commercialsOffersCountForSale: props.region.stats?.offers_commercials_count_for_sale || 0,
        commercialsPropertiesCountForSale: props.region.stats?.properties_commercials_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0
    };

    return [
        {
            headline: `Nowe lokale użytkowe na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.commercialsOffersCountForSale,
                    propertiesCountForSale: variables.commercialsPropertiesCountForSale,
                    offerTypeName: "nowych lokali użytkowych"
                }),
                ...getCommercialsRoomRangeSectionTexts(props)
            ]
        },
        {
            headline: `Ile kosztują nowe lokale użytkowe ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        variables.commercialPriceMin > 0 &&
                            `Najtańszy lokal użytkowy ${variables.regionNameDeclensionWhere} dostępny w sprzedaży kosztuje ${priceFormat(
                                variables.commercialPriceMin
                            )}. `,
                        variables.commercialsPriceM2Avg > 0 &&
                            `Aktualnie średnia cena za m2 lokali użytkowych ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(
                                variables.commercialsPriceM2Avg
                            )}.`
                    ].filter(Boolean) as string[]
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach lokali użytkowych ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na lokale użytkowe.`
                      }
            ]
        },
        {
            headline: `${variables.regionName} - lokale użytkowe gotowe do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountForSale: variables.commercialsPropertiesCountForSale,
                    offersCountReady: variables.commercialsCountReady,
                    offersCountReadyInThreeMonths: variables.commercialsCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.commercialsCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.commercialsCountReadyInOneYear,
                    offerTypeNamePluralize: ["lokal użytkowy", "lokale użytkowe", "lokali użytkowych"],
                    offerTypeNameWithDeclensionOfReadyPluralize: [
                        "lokal użytkowy zostanie oddany",
                        "lokale użytkowe zostaną oddane",
                        "lokali użytkowych zostanie oddanych"
                    ],
                    offersLatestConstructionDate: variables.commercialsLatestConstructionDate
                }),
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Szukasz atrakcyjnej nieruchomości do zamieszkania? Sprawdź `,
                        variables.flatsPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${variables.regionSlug}`}),
                                  text: `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych mieszkań ${variables.regionNameDeclensionWhere}`,
                        ` i `,
                        variables.housesPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${variables.regionSlug}`}),
                                  text: `nowych domów ${variables.regionNameDeclensionWhere}`
                              }
                            : `nowych domów ${variables.regionNameDeclensionWhere}`,
                        `.`
                    ]
                }
            ]
        }
    ];
};
